import React from "react";
import { Button, Card, Dropdown, Input, Option, Spinner, Text } from "@fluentui/react-components";

import { apiUrl } from "../../utils/api";

/**
 * Index-Stammdaten der Neuwertberechnung pflegen.
 *
 * Diese Zahlen sind der Multiplikator jeder Versicherungssumme, die der
 * Anlagen-Import berechnet:
 *
 *     Neuwert = AHK × Index[Sparte][Zieljahr] / Index[Sparte][Anschaffungsjahr]
 *
 * Ein falscher Wert fällt nirgends auf — die Rechnung geht durch, die Summe
 * sieht plausibel aus. Deshalb steht der bisherige Wert beim Speichern in der
 * Beschriftung, und ein neues Jahr lässt sich nur lückenlos anfügen.
 *
 * Löschen gibt es bewusst nicht: Ein entfernter Jahreswert macht jede Position
 * dieses Jahrgangs unrechenbar, und das zeigte sich erst beim nächsten Import.
 */

interface IndexReihe {
  sparte: string;
  werte: Array<{ jahr: number; wert: number }>;
}

const nachricht = async (res: Response): Promise<string> => {
  try {
    const body = await res.json();
    if (body?.message) return Array.isArray(body.message) ? body.message.join("; ") : body.message;
  } catch {
    /* Antwort ohne JSON-Körper — dann bleibt es beim Status. */
  }
  return `HTTP ${res.status}`;
};

const NeuwertIndexPflege: React.FC = () => {
  const [reihen, setReihen] = React.useState<IndexReihe[]>([]);
  const [sparte, setSparte] = React.useState<string>("");
  const [laedt, setLaedt] = React.useState(true);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [erfolg, setErfolg] = React.useState<string | null>(null);
  const [entwurf, setEntwurf] = React.useState<Record<number, string>>({});
  const [neuesJahr, setNeuesJahr] = React.useState("");
  const [neuerWert, setNeuerWert] = React.useState("");
  const [speichert, setSpeichert] = React.useState(false);

  const laden = React.useCallback(async () => {
    setLaedt(true);
    try {
      const res = await fetch(apiUrl("neuwert-index"));
      if (!res.ok) throw new Error(await nachricht(res));
      const daten: IndexReihe[] = await res.json();
      setReihen(daten);
      setSparte((bisher) => bisher || daten[0]?.sparte || "");
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(false);
    }
  }, []);

  React.useEffect(() => {
    void laden();
  }, [laden]);

  const aktuelle = reihen.find((r) => r.sparte === sparte);

  const schreiben = async (methode: "PUT" | "POST", jahr: number, wert: number) => {
    setSpeichert(true);
    setFehler(null);
    setErfolg(null);
    try {
      const res = await fetch(apiUrl("neuwert-index"), {
        method: methode,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sparte, jahr, wert }),
      });
      if (!res.ok) throw new Error(await nachricht(res));
      setErfolg(
        methode === "PUT"
          ? `${sparte} ${jahr} auf ${wert} gesetzt.`
          : `${sparte} ${jahr} mit ${wert} ergänzt.`,
      );
      setEntwurf({});
      setNeuesJahr("");
      setNeuerWert("");
      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  };

  const naechstesJahr = aktuelle?.werte.length
    ? Math.max(...aktuelle.werte.map((w) => w.jahr)) + 1
    : undefined;

  return (
    <Card
      style={{ padding: 16, border: "1px solid #ececec", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <Text style={{ fontWeight: 600 }}>Neuwert-Indizes</Text>
      <Text style={{ color: "#666", fontSize: 13 }}>
        Grundlage der Neuwertberechnung im Anlagen-Import: Die AHK einer Position wird mit dem
        Verhältnis zweier Jahreswerte hochgerechnet. Ein geänderter Wert wirkt auf jeden Import
        danach — bereits angelegte Positionen bleiben, wie sie sind.
      </Text>

      {laedt && <Spinner size="tiny" label="Reihen werden geladen" />}
      {fehler && <Text style={{ color: "#b10e1c", fontSize: 13 }}>{fehler}</Text>}
      {erfolg && <Text style={{ color: "#0f6d31", fontSize: 13 }}>{erfolg}</Text>}

      {!laedt && reihen.length > 0 && (
        <>
          <Dropdown
            value={sparte}
            selectedOptions={[sparte]}
            onOptionSelect={(_, d) => {
              setSparte(String(d.optionValue));
              setEntwurf({});
              setErfolg(null);
            }}
          >
            {reihen.map((r) => (
              <Option key={r.sparte} value={r.sparte}>
                {`${r.sparte} (${r.werte.length} Jahre)`}
              </Option>
            ))}
          </Dropdown>

          {aktuelle && (
            <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #ececec", borderRadius: 4 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px 10px", position: "sticky", top: 0, background: "#fff" }}>
                      Jahr
                    </th>
                    <th style={{ textAlign: "right", padding: "6px 10px", position: "sticky", top: 0, background: "#fff" }}>
                      Wert
                    </th>
                    <th style={{ padding: "6px 10px", position: "sticky", top: 0, background: "#fff" }} />
                  </tr>
                </thead>
                <tbody>
                  {/* Jüngste Jahre zuerst: Der Indexstand des laufenden Jahres wird
                      am häufigsten gepflegt. */}
                  {[...aktuelle.werte].reverse().map((w) => {
                    const wert = entwurf[w.jahr] ?? String(w.wert);
                    const geaendert = entwurf[w.jahr] !== undefined && Number(entwurf[w.jahr]) !== w.wert;
                    return (
                      <tr key={w.jahr}>
                        <td style={{ padding: "4px 10px", borderTop: "1px solid #f3f3f3" }}>{w.jahr}</td>
                        <td style={{ padding: "4px 10px", borderTop: "1px solid #f3f3f3", textAlign: "right" }}>
                          <Input
                            size="small"
                            value={wert}
                            style={{ width: 120 }}
                            onChange={(_, d) => setEntwurf({ ...entwurf, [w.jahr]: d.value })}
                          />
                        </td>
                        <td style={{ padding: "4px 10px", borderTop: "1px solid #f3f3f3" }}>
                          {geaendert && (
                            <Button
                              size="small"
                              appearance="primary"
                              disabled={speichert || !Number.isFinite(Number(wert)) || Number(wert) <= 0}
                              onClick={() => schreiben("PUT", w.jahr, Number(wert))}
                            >
                              {`${w.wert} → ${wert} speichern`}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 4 }}>
              <Text style={{ fontSize: 12, color: "#666" }}>Jahr ergänzen</Text>
              <Input
                size="small"
                style={{ width: 100 }}
                value={neuesJahr}
                placeholder={naechstesJahr ? String(naechstesJahr) : ""}
                onChange={(_, d) => setNeuesJahr(d.value)}
              />
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              <Text style={{ fontSize: 12, color: "#666" }}>Wert</Text>
              <Input
                size="small"
                style={{ width: 120 }}
                value={neuerWert}
                onChange={(_, d) => setNeuerWert(d.value)}
              />
            </div>
            <Button
              size="small"
              disabled={
                speichert ||
                !Number.isInteger(Number(neuesJahr)) ||
                !Number.isFinite(Number(neuerWert)) ||
                Number(neuerWert) <= 0
              }
              onClick={() => schreiben("POST", Number(neuesJahr), Number(neuerWert))}
            >
              Ergänzen
            </Button>
            <Text style={{ fontSize: 12, color: "#666" }}>
              Nur lückenlos anschließend — eine Lücke macht jede Position dieses Jahrgangs
              unrechenbar.
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

export default NeuwertIndexPflege;
